import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TollOutlinedIcon from "@mui/icons-material/TollOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  CREDITS_INFO_TOOLTIP,
  CREDIT_DOTTED_SEGMENT_COUNT,
  CREDIT_USAGE_STATUS,
  formatCreditCount,
} from "../../helper/creditBalanceModel";
import CreditsDottedProgress from "./CreditsDottedProgress";
import { MOBILE_PAGE_PAD_X } from "../../theme/mobileLayout";

function getRemainingCopy(balance, error) {
  if (error) return "Unavailable";
  if (!balance || balance.totalAvailable === null) return "—";
  if (balance.totalAvailable <= 0) return "0 left";
  return `${formatCreditCount(balance.totalAvailable)} left`;
}

/**
 * Quick Usage overview for the V2 account dropdown / mobile menu.
 */
export default function AccountMenuCreditsSection({
  balance,
  loading = false,
  error = null,
  onOpenUsage,
  onTopUp,
  compact = false,
  variant = "menu",
  embeddedInMenuItem = false,
  helpTextId = null,
}) {
  const theme = useTheme();
  const isMobile = variant === "mobile";
  const status = balance?.status || null;
  const hasDeterminateProgress = Number.isFinite(balance?.remainingRatio);
  const remainingRatio = hasDeterminateProgress ? balance.remainingRatio : 0;
  const remainingCopy = getRemainingCopy(balance, error);

  const remainingColor =
    status === CREDIT_USAGE_STATUS.LOW || status === CREDIT_USAGE_STATUS.EXHAUSTED
      ? "error.main"
      : isMobile && status === CREDIT_USAGE_STATUS.MEDIUM
        ? "warning.main"
        : "text.secondary";

  return (
    <Box
      data-testid="account-menu-credits-section"
      data-variant={variant}
      sx={{
        px: isMobile ? MOBILE_PAGE_PAD_X : compact ? 2 : 1.5,
        pt: isMobile ? 0.5 : compact ? 1.25 : 1,
        pb: isMobile ? 0.25 : compact ? 1 : 0.75,
      }}
    >
      <Box
        onClick={embeddedInMenuItem ? undefined : onOpenUsage}
        onKeyDown={embeddedInMenuItem ? undefined : (event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpenUsage();
          }
        }}
        role={embeddedInMenuItem ? undefined : "button"}
        tabIndex={embeddedInMenuItem ? undefined : 0}
        aria-label={embeddedInMenuItem ? undefined : "Open usage settings"}
        sx={{
          width: "100%",
          m: 0,
          p: 0,
          border: 0,
          background: "transparent",
          textAlign: "left",
          cursor: "pointer",
          color: "inherit",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mb: isMobile ? 1 : 0.75,
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.375, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: isMobile ? 13 : 12.5,
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              Credits
            </Typography>
            {embeddedInMenuItem ? (
              <>
                <InfoOutlinedIcon
                  aria-hidden="true"
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
                {helpTextId ? (
                  <Box
                    component="span"
                    id={helpTextId}
                    sx={{
                      position: "absolute",
                      width: 1,
                      height: 1,
                      p: 0,
                      m: -1,
                      overflow: "hidden",
                      clip: "rect(0 0 0 0)",
                      whiteSpace: "nowrap",
                      border: 0,
                    }}
                  >
                    {CREDITS_INFO_TOOLTIP}
                  </Box>
                ) : null}
              </>
            ) : (
              <Tooltip
                title={CREDITS_INFO_TOOLTIP}
                enterTouchDelay={0}
                leaveTouchDelay={4000}
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  aria-label="What are credits?"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                  sx={{
                    p: 0.25,
                    color: "text.secondary",
                    "&:hover": { bgcolor: "rgba(74, 44, 245, 0.06)" },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.125, flexShrink: 0 }}>
            {loading ? (
              <CircularProgress size={14} aria-label="Loading credits" />
            ) : (
              <Typography
                sx={{
                  fontSize: isMobile ? 12.5 : 12,
                  fontWeight: 600,
                  color: remainingColor,
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                }}
              >
                {remainingCopy}
              </Typography>
            )}
            <ChevronRightIcon sx={{ fontSize: 16, color: "text.secondary", opacity: 0.72 }} />
          </Box>
        </Box>

        {loading || error || !balance || !hasDeterminateProgress ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: isMobile ? "space-between" : "flex-start",
              gap: isMobile ? 0 : 0.625,
            }}
          >
            {Array.from({ length: CREDIT_DOTTED_SEGMENT_COUNT }, (_, index) => (
              <Box
                key={index}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(88, 89, 91, 0.12)",
                }}
              />
            ))}
          </Box>
        ) : (
          <CreditsDottedProgress
            remainingRatio={remainingRatio}
            status={status}
            layout="spread"
          />
        )}
      </Box>

      {balance?.trialDaysRemaining != null ? (
        <Typography
          data-testid="account-menu-trial-days"
          sx={{
            mt: 0.875,
            color: "text.secondary",
            fontSize: isMobile ? 12.5 : 12,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {`${balance.trialDaysRemaining} ${balance.trialDaysRemaining === 1 ? "day" : "days"} remaining`}
        </Typography>
      ) : null}

      {!embeddedInMenuItem ? (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          size="small"
          startIcon={
            <TollOutlinedIcon
              sx={{ fontSize: isMobile ? "16px !important" : "18px !important" }}
            />
          }
          onClick={onTopUp}
          sx={{
            mt: 1.25,
            justifyContent: "center",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 12.5,
            minHeight: 36,
            py: 0.75,
            px: 1.5,
            borderRadius: "10px",
          }}
        >
          Top Up Credits
        </Button>
      ) : null}
    </Box>
  );
}

AccountMenuCreditsSection.propTypes = {
  balance: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onOpenUsage: PropTypes.func.isRequired,
  onTopUp: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  variant: PropTypes.oneOf(["menu", "mobile"]),
  embeddedInMenuItem: PropTypes.bool,
  helpTextId: PropTypes.string,
};
